/**
 * Shrinks a photo taken on site before it is uploaded.
 *
 * The handover note asks for six photos of a machine, and a modern phone
 * produces 3-6 MB each. That is 20-35 MB in one multipart request, sent
 * from a building site with one bar of signal -- which is not a rare
 * failure, it is the normal one. A 1600px JPEG at 80% shows a scratch on a
 * bucket just as clearly and weighs about 400 KB, so the same walkaround
 * goes up in roughly a tenth of the bytes and survives a connection that
 * would have dropped the original.
 *
 * Re-encoding drops the file's EXIF, including its GPS tag and capture
 * time, and that is fine HERE specifically: a handover report already
 * records the device's coordinates and the server's own timestamp on the
 * row itself (see CheckInOutReport.Latitude/Longitude/SubmittedAt). Those
 * are better evidence than metadata inside a file, which anyone can edit.
 * This helper is for that flow, not a general-purpose uploader.
 *
 * Fails safe at every step: anything it cannot probe, decode or re-encode
 * gives back the original File untouched. A photo that uploads slowly is a
 * nuisance; a handover that cannot be completed is a lorry going home with
 * a machine still on it.
 */

/** Longest edge after shrinking. Enough to read an hour meter and see surface damage. */
const MAX_EDGE = 1600;

/** JPEG quality. Below ~0.75 compression artefacts start to look like scratches, which is the one thing these photos exist to show. */
const QUALITY = 0.8;

/** Below this, re-encoding buys little and can even grow the file. */
const SKIP_BELOW_BYTES = 700 * 1024;

/**
 * A 4x2 JPEG carrying nothing but an EXIF orientation of 6 ("rotate a
 * quarter turn clockwise"). A browser that applies the tag while decoding
 * reports it as 2 wide by 4 tall; one that ignores it reports 4 by 2.
 *
 * This exists because compressing DESTROYS that tag: the pixels the canvas
 * writes are final, so they have to come out of the decoder already the
 * right way up. If they do not, every portrait photo on every handover note
 * ends up on its side, with nothing left in the file for anyone to correct
 * it from -- so the probe below decides whether compressing is safe at all,
 * rather than assuming a browser behaves.
 */
const ORIENTATION_PROBE = '/9j/4QAiRXhpZgAASUkqAAgAAAABABIBAwABAAAABgAAAAAAAAD/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDAAMCAgMCAgMDAwMEAwMEBQgFBQQEBQoHBwYIDAoMDAsKCwsNDhIQDQ4RDgsLEBYQERMUFRUVDA8XGBYUGBIUFRT/2wBDAQMEBAUEBQkFBQkUDQsNFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBT/wAARCAACAAQDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAj/xAAaEAADAQADAAAAAAAAAAAAAAABAgMAETFB/8QAFQEBAQAAAAAAAAAAAAAAAAAABwn/xAAcEQABAwUAAAAAAAAAAAAAAAAAAQIDBTI0crH/2gAMAwEAAhEDEQA/AJPrV71elHalHJZnc8lieyT6czMQstQoBT8OHVvEP//Z';

const toBlob = (base64: string, type: string): Blob => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type });
};

// Run once per page load, not once per photo -- the answer cannot change
// under the same browser, and six photos should not each pay for it.
let orientationProbe: Promise<boolean> | null = null;

const decoderRespectsOrientation = (): Promise<boolean> => {
  if (orientationProbe) return orientationProbe;
  orientationProbe = (async () => {
    try {
      if (typeof createImageBitmap !== 'function') return false;
      const bitmap = await createImageBitmap(toBlob(ORIENTATION_PROBE, 'image/jpeg'));
      const upright = bitmap.width === 2 && bitmap.height === 4;
      bitmap.close();
      return upright;
    } catch {
      return false;
    }
  })();
  return orientationProbe;
};

/**
 * The shrunk photo, or the original when shrinking is unnecessary,
 * unsupported or unsafe. Always a File, never throws -- callers upload
 * whatever comes back without checking which of the two they got.
 */
export const compressPhoto = async (file: File): Promise<File> => {
  try {
    if (file.size <= SKIP_BELOW_BYTES) return file;
    if (!(await decoderRespectsOrientation())) return file;

    const bitmap = await createImageBitmap(file);
    // Never upscale: a small photo stays its own size and only loses weight.
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      bitmap.close();
      return file;
    }
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
    if (!blob) return file;
    // A photo that grew is a photo not worth replacing.
    if (blob.size >= file.size) return file;

    // JPEG name and type on purpose: the API accepts JPEG or PNG only, and
    // whatever came in has just been re-encoded as JPEG.
    const name = file.name.replace(/\.[^.]+$/, '') || 'photo';
    return new File([blob], `${name}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return file;
  }
};
